import { createContext, use, useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, FalhaDaApi } from '../api/cliente';

/**
 * Sessão da CLÍNICA — separada da sessão do escritório de ponta a ponta.
 *
 * São dois cookies, duas guardas e dois provedores. Um provedor só, com um `if`,
 * acabaria deixando a recepção da clínica herdar um pedaço da tela da equipe no dia
 * em que alguém esquecesse uma verificação.
 *
 * Aqui também não há renovação automática: a sessão do escritório dura trinta dias
 * porque a colaboradora fica o dia inteiro no sistema; a da clínica é curta de
 * propósito, e expirar pedindo a senha de novo é o comportamento desejado num
 * computador de recepção compartilhado.
 */
export interface ClinicaDaSessao {
  readonly id: string;
  readonly nome: string;
}

interface Sessao {
  /**
   * Nome de quem está no portal: a clínica, quando é uma só; o dentista, quando o
   * acesso cobre mais de uma razão social.
   */
  readonly titular: string | null;
  /** As carteiras deste acesso. Mais de uma quando o dentista tem duas clínicas. */
  readonly clinicas: readonly ClinicaDaSessao[];
  /** Quando falso, o portal não mostra nenhum valor recuperado. */
  readonly verFinanceiro: boolean;
  readonly carregando: boolean;
  entrar(token: string, senha: string): Promise<void>;
  sair(): Promise<void>;
}

const Contexto = createContext<Sessao | null>(null);

interface RespostaDaSessao {
  readonly titular: string;
  readonly clinicas: ClinicaDaSessao[];
  readonly verFinanceiro: boolean;
}

export function SessaoPortalProvedor({ children }: { children: ReactNode }) {
  const [titular, setTitular] = useState<string | null>(null);
  const [clinicas, setClinicas] = useState<readonly ClinicaDaSessao[]>([]);
  const [verFinanceiro, setVerFinanceiro] = useState(false);
  const [carregando, setCarregando] = useState(true);

  /**
   * Ao abrir a página, pergunta ao servidor quem está nesta sessão.
   *
   * O cookie é `HttpOnly` — o script não consegue lê-lo. Sem esta pergunta, um F5
   * jogaria a recepção de volta para o login com a sessão ainda válida.
   */
  useEffect(() => {
    let ativo = true;

    async function restaurar() {
      try {
        const r = await api.buscar<RespostaDaSessao>('/portal/eu');
        if (!ativo) return;
        setTitular(r.titular);
        setClinicas(r.clinicas);
        setVerFinanceiro(r.verFinanceiro);
      } catch {
        // Qualquer falha aqui significa "não há sessão" — inclusive o 401 esperado
        // de quem está abrindo o link pela primeira vez.
        if (ativo) setTitular(null);
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    void restaurar();
    return () => {
      ativo = false;
    };
  }, []);

  const entrar = useCallback(async (token: string, senha: string) => {
    const r = await api.criar<RespostaDaSessao>('/portal/entrar', { token, senha });
    setTitular(r.titular);
    setClinicas(r.clinicas);
    setVerFinanceiro(r.verFinanceiro);
  }, []);

  const sair = useCallback(async () => {
    try {
      await api.criar('/portal/sair');
    } catch (erro) {
      // Sessão já expirada no servidor devolve erro, e sair mesmo assim é o certo:
      // insistir na tela por causa disso deixaria a recepção presa numa sessão morta.
      if (!(erro instanceof FalhaDaApi)) throw erro;
    }
    setTitular(null);
    setClinicas([]);
    setVerFinanceiro(false);
  }, []);

  return (
    <Contexto value={{ titular, clinicas, verFinanceiro, carregando, entrar, sair }}>
      {children}
    </Contexto>
  );
}

export function useSessaoPortal(): Sessao {
  const sessao = use(Contexto);
  if (!sessao) throw new Error('useSessaoPortal só funciona dentro de SessaoPortalProvedor.');
  return sessao;
}
